import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ArrowRight, Sparkles } from 'lucide-react';
import { Card, Button, Badge } from '../ui';
import { services } from '../../data/services';
import { formatCurrency } from '../../utils/formatting';

const Services = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-24 bg-soft-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-pragyavan-gold/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-sage-green/5 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pragyavan-gold/10 text-pragyavan-brown text-xs font-medium mb-4">
            <Sparkles className="w-3 h-3" />
            <span>Holistic Wellness</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-pragyavan-brown mb-6">
            Nurture Your <span className="text-gradient-gold">Spirit</span>
          </h2>
          <p className="text-lg text-warm-gray max-w-2xl mx-auto leading-relaxed">
            Choose from our carefully curated wellness programs, each designed to
            guide you on your journey to inner peace and physical vitality.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
        >
          {services.map((service) => (
            <motion.div
              key={service.id}
              variants={itemVariants}
              whileHover={{ y: -8 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <Card
                className="h-full relative overflow-hidden group border-0 shadow-glass hover:shadow-premium bg-white"
                padding="none"
                rounded="full"
              >
                {/* Service Image Area */}
                <div className="h-56 bg-gradient-to-br from-pragyavan-gold/10 to-sage-green/10 relative overflow-hidden group-hover:scale-105 transition-transform duration-700">
                  {service.image ? (
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-pragyavan-brown/40">
                        <div className="w-20 h-20 mx-auto mb-3 rounded-full bg-white/40 backdrop-blur-sm flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-500">
                          <div className="w-10 h-10 rounded-full bg-pragyavan-brown/10"></div>
                        </div>
                        <p className="text-xs font-medium tracking-widest uppercase">{service.title}</p>
                      </div>
                    </div>
                  )}

                  {/* Badge */}
                  {service.badge && (
                    <div className="absolute top-4 right-4 z-10">
                      <Badge
                        variant={service.badge === 'Popular' ? 'popular' : 'premium'}
                        size="small"
                        className="shadow-sm backdrop-blur-md bg-white/80"
                      >
                        {service.badge}
                      </Badge>
                    </div>
                  )}
                </div>

                <div className="p-8">
                  {/* Service Details */}
                  <div className="mb-6">
                    <h3 className="text-2xl font-display font-semibold text-pragyavan-brown mb-3 group-hover:text-pragyavan-gold transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-warm-gray text-sm leading-relaxed mb-6 line-clamp-3">
                      {service.description}
                    </p>
                  </div>

                  {/* Price and Duration */}
                  <div className="flex items-center justify-between mb-6 pb-6 border-b border-warm-gray/10">
                    <div className="text-3xl font-bold text-pragyavan-brown">
                      {formatCurrency(service.price)}
                    </div>
                    <div className="flex items-center text-warm-gray text-sm bg-warm-gray/5 px-3 py-1 rounded-full">
                      <Clock className="w-4 h-4 mr-1.5" />
                      {service.duration}
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-8">
                    <ul className="space-y-3">
                      {service.features?.slice(0, 3).map((feature, index) => (
                        <li key={index} className="text-sm text-warm-gray flex items-start group/item">
                          <div className="w-5 h-5 rounded-full bg-pragyavan-gold/10 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5 group-hover/item:bg-pragyavan-gold/20 transition-colors duration-300">
                            <svg className="w-3 h-3 text-pragyavan-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span className="group-hover/item:text-pragyavan-brown transition-colors duration-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link to="/book" state={{ selectedService: service.id }}>
                    <Button
                      variant="outline"
                      size="medium"
                      fullWidth
                      icon={ArrowRight}
                      iconPosition="right"
                      className="group-hover:bg-pragyavan-brown group-hover:text-white group-hover:border-pragyavan-brown transition-all duration-300"
                    >
                      Book Now
                    </Button>
                  </Link>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* View All Services CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center"
        >
          <Link to="/book">
            <Button variant="secondary" size="large" className="min-w-[200px]">
              View All Services
            </Button>
          </Link>
          <p className="text-sm text-warm-gray mt-4 opacity-60">
            Discover more wellness programs tailored to your needs
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;